<template>
  <div class="physical-test-data-view">
    <div class="section-header">
      <h3>体测数据查询</h3>
      <div class="filter-controls">
        <el-select 
          v-model="selectedTest" 
          placeholder="选择体测" 
          clearable
          @change="handleTestChange"
          style="width: 200px; margin-right: 10px;"
        >
          <el-option
            v-for="test in tests"
            :key="test.test_id"
            :label="test.test_name"
            :value="test.test_id">
          </el-option>
        </el-select>
        <el-button type="primary" @click="loadData" :loading="loading">
          <i class="el-icon-search"></i> 查询
        </el-button>
      </div>
    </div>

    <div class="data-table-container">
      <el-table 
        :data="tableData" 
        v-loading="loading"
        stripe
        border
        style="width: 100%"
        :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
      >
        <el-table-column prop="test_name" label="测试名称" width="150">
          <template slot-scope="scope">
            <el-tag type="info" size="small">{{ scope.row.test_name }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="item_name" label="运动项目" width="120"></el-table-column>
        <el-table-column label="测试成绩" width="120" align="center">
          <template slot-scope="scope">
            <div v-if="scope.row.is_absent" class="absent-mark">
              <el-tag type="info" size="small">缺席</el-tag>
            </div>
            <div v-else class="score-display">
              <span class="score-value">{{ scope.row.score_display }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="计算分数" width="100" align="center">
          <template slot-scope="scope">
            <span v-if="!scope.row.is_absent && scope.row.calculated_score" 
                  class="calculated-score"
                  :class="getScoreClass(scope.row.calculated_score, scope.row.pass_standard, scope.row.excellent_standard)">
              {{ scope.row.calculated_score }}
            </span>
            <span v-else class="no-score">-</span>
          </template>
        </el-table-column>
        <el-table-column label="标准" width="120" align="center">
          <template slot-scope="scope">
            <div class="standards">
              <div class="standard-item">
                <span class="standard-label">及格:</span>
                <span class="standard-value">{{ scope.row.pass_standard }}</span>
              </div>
              <div class="standard-item">
                <span class="standard-label">优秀:</span>
                <span class="standard-value">{{ scope.row.excellent_standard }}</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="test_date" label="测试日期" width="120" align="center"></el-table-column>
        <el-table-column prop="remarks" label="备注" min-width="150">
          <template slot-scope="scope">
            <span class="remarks-text">
              {{ scope.row.remarks || '无备注' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" align="center">
          <template slot-scope="scope">
            <el-button 
              type="text" 
              size="small" 
              @click="showDetail(scope.row)"
            >
              详情
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total">
        </el-pagination>
      </div>
    </div>

    <!-- 详情对话框 -->
    <el-dialog title="体测成绩详情" :visible.sync="detailDialogVisible" width="600px">
      <div v-if="selectedRecord" class="detail-content">
        <div class="detail-item">
          <label>测试名称:</label>
          <span>{{ selectedRecord.test_name }}</span>
        </div>
        <div class="detail-item">
          <label>运动项目:</label>
          <span>{{ selectedRecord.item_name }}</span>
        </div>
        <div class="detail-item">
          <label>测试状态:</label>
          <el-tag :type="selectedRecord.is_absent ? 'info' : 'success'" size="medium">
            {{ selectedRecord.is_absent ? '缺席' : '正常参加' }}
          </el-tag>
        </div>
        <div class="detail-item" v-if="!selectedRecord.is_absent">
          <label>原始成绩:</label>
          <span class="score-highlight">{{ selectedRecord.score_display }}</span>
        </div>
        <div class="detail-item" v-if="!selectedRecord.is_absent && selectedRecord.calculated_score">
          <label>计算分数:</label>
          <span class="score-highlight" 
                :class="getScoreClass(selectedRecord.calculated_score, selectedRecord.pass_standard, selectedRecord.excellent_standard)">
            {{ selectedRecord.calculated_score }}
          </span>
        </div>
        <div class="detail-item">
          <label>及格标准:</label>
          <span>{{ selectedRecord.pass_standard }}{{ selectedRecord.measurement_unit || '' }}</span>
        </div>
        <div class="detail-item">
          <label>优秀标准:</label>
          <span>{{ selectedRecord.excellent_standard }}{{ selectedRecord.measurement_unit || '' }}</span>
        </div>
        <div class="detail-item">
          <label>测试日期:</label>
          <span>{{ selectedRecord.test_date }}</span>
        </div>
        <div class="detail-item">
          <label>记录时间:</label>
          <span>{{ selectedRecord.created_at }}</span>
        </div>
        <div class="detail-item" v-if="selectedRecord.remarks">
          <label>备注:</label>
          <p class="remarks-text">{{ selectedRecord.remarks }}</p>
        </div>
        <div class="detail-item" v-if="selectedRecord.test_description">
          <label>测试描述:</label>
          <p class="description-text">{{ selectedRecord.test_description }}</p>
        </div>
        
        <!-- 成绩评估 -->
        <div class="detail-item" v-if="!selectedRecord.is_absent && selectedRecord.calculated_score">
          <label>成绩评估:</label>
          <div class="performance-assessment">
            <div class="assessment-level">
              <el-tag 
                :type="getScoreTagType(selectedRecord.calculated_score, selectedRecord.pass_standard, selectedRecord.excellent_standard)"
                size="medium"
              >
                {{ getPerformanceLevel(selectedRecord.calculated_score, selectedRecord.pass_standard, selectedRecord.excellent_standard) }}
              </el-tag>
            </div>
            <div class="assessment-desc">
              {{ getPerformanceDescription(selectedRecord.calculated_score, selectedRecord.pass_standard, selectedRecord.excellent_standard) }}
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 体测统计分析 -->
    <div class="statistics-container" v-if="tableData.length > 0">
      <h4>体测统计分析</h4>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="card-header">
            <i class="el-icon-data-analysis"></i>
            <span>总测试项目</span>
          </div>
          <div class="card-value">{{ totalItems }}</div>
          <div class="card-desc">个项目</div>
        </div>
        <div class="stat-card">
          <div class="card-header">
            <i class="el-icon-success"></i>
            <span>及格项目</span>
          </div>
          <div class="card-value">{{ passedItems }}</div>
          <div class="card-desc">{{ passRate }}% 及格率</div>
        </div>
        <div class="stat-card">
          <div class="card-header">
            <i class="el-icon-star-on"></i>
            <span>优秀项目</span>
          </div>
          <div class="card-value">{{ excellentItems }}</div>
          <div class="card-desc">{{ excellentRate }}% 优秀率</div>
        </div>
        <div class="stat-card">
          <div class="card-header">
            <i class="el-icon-warning"></i>
            <span>缺席次数</span>
          </div>
          <div class="card-value">{{ absentItems }}</div>
          <div class="card-desc">次缺席</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getPhysicalTestData, getPhysicalTests } from '../../../api/dataQueryApi'

export default {
  name: 'PhysicalTestDataView',
  data() {
    return {
      loading: false,
      tableData: [],
      tests: [],
      selectedTest: null,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      detailDialogVisible: false,
      selectedRecord: null
    }
  },
  computed: {
    totalItems() {
      return this.tableData.length
    },
    passedItems() {
      return this.tableData.filter(item => 
        !item.is_absent && 
        item.calculated_score && 
        parseFloat(item.calculated_score) >= parseFloat(item.pass_standard)
      ).length
    },
    excellentItems() {
      return this.tableData.filter(item => 
        !item.is_absent && 
        item.calculated_score && 
        parseFloat(item.calculated_score) >= parseFloat(item.excellent_standard)
      ).length
    },
    absentItems() {
      return this.tableData.filter(item => item.is_absent).length
    },
    passRate() {
      const validItems = this.tableData.filter(item => !item.is_absent).length
      return validItems > 0 ? Math.round((this.passedItems / validItems) * 100) : 0
    },
    excellentRate() {
      const validItems = this.tableData.filter(item => !item.is_absent).length
      return validItems > 0 ? Math.round((this.excellentItems / validItems) * 100) : 0
    }
  },
  mounted() {
    this.loadTests()
    this.loadData()
  },
  methods: {
    async loadTests() {
      try {
        const response = await getPhysicalTests()
        this.tests = response.data
      } catch (error) {
        console.error('获取体测列表失败:', error)
      }
    },
    async loadData() {
      try {
        this.loading = true
        const params = {
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        }
        
        if (this.selectedTest) {
          params.test_id = this.selectedTest
        }
        
        const response = await getPhysicalTestData(params)
        this.tableData = response.data.results
        this.pagination.total = response.data.total
      } catch (error) {
        console.error('获取体测数据失败:', error)
        this.$message.error(error.message || '获取体测数据失败')
      } finally {
        this.loading = false
      }
    },
    handleTestChange() {
      this.pagination.page = 1
      this.loadData()
    },
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.page = 1
      this.loadData()
    },
    handleCurrentChange(val) {
      this.pagination.page = val
      this.loadData()
    },
    showDetail(row) {
      this.selectedRecord = row
      this.detailDialogVisible = true
    },
    getScoreClass(score, passStandard, excellentStandard) {
      const numScore = parseFloat(score)
      const numPass = parseFloat(passStandard)
      const numExcellent = parseFloat(excellentStandard)
      
      if (numScore >= numExcellent) {
        return 'score-excellent'
      } else if (numScore >= numPass) {
        return 'score-pass'
      } else {
        return 'score-fail'
      }
    },
    getScoreTagType(score, passStandard, excellentStandard) {
      const numScore = parseFloat(score)
      const numPass = parseFloat(passStandard)
      const numExcellent = parseFloat(excellentStandard)
      
      if (numScore >= numExcellent) {
        return 'success'
      } else if (numScore >= numPass) {
        return 'warning'
      } else {
        return 'danger'
      }
    },
    getPerformanceLevel(score, passStandard, excellentStandard) {
      const numScore = parseFloat(score)
      const numPass = parseFloat(passStandard)
      const numExcellent = parseFloat(excellentStandard)
      
      if (numScore >= numExcellent) {
        return '优秀'
      } else if (numScore >= numPass) {
        return '及格'
      } else {
        return '不及格'
      }
    },
    getPerformanceDescription(score, passStandard, excellentStandard) {
      const numScore = parseFloat(score)
      const numPass = parseFloat(passStandard)
      const numExcellent = parseFloat(excellentStandard)
      
      if (numScore >= numExcellent) {
        return '体能表现优秀，继续保持良好的锻炼习惯'
      } else if (numScore >= numPass) {
        return '体能表现合格，建议加强相关项目的训练'
      } else {
        return '体能表现需要改进，建议制定针对性的训练计划'
      }
    }
  }
}
</script>

<style scoped>
.physical-test-data-view {
  padding: 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ebeef5;
}

.section-header h3 {
  color: #333;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.filter-controls {
  display: flex;
  align-items: center;
}

.data-table-container {
  background: white;
}

.absent-mark {
  display: flex;
  justify-content: center;
}

.score-display {
  text-align: center;
}

.score-value {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.calculated-score {
  font-weight: 600;
  font-size: 16px;
}

.score-excellent {
  color: #67c23a;
}

.score-pass {
  color: #e6a23c;
}

.score-fail {
  color: #f56c6c;
}

.no-score {
  color: #c0c4cc;
}

.standards {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.standard-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.standard-label {
  color: #909399;
}

.standard-value {
  color: #333;
  font-weight: 500;
}

.remarks-text {
  color: #666;
  font-size: 13px;
  line-height: 1.4;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.detail-content {
  padding: 10px 0;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item label {
  width: 100px;
  color: #666;
  font-weight: 500;
  flex-shrink: 0;
}

.detail-item span {
  color: #333;
  flex: 1;
}

.score-highlight {
  font-size: 18px;
  font-weight: 600;
}

.performance-assessment {
  flex: 1;
}

.assessment-level {
  margin-bottom: 8px;
}

.assessment-desc {
  color: #666;
  font-size: 14px;
  line-height: 1.4;
}

.description-text {
  margin: 0;
  line-height: 1.6;
  color: #666;
}

.statistics-container {
  margin-top: 30px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.statistics-container h4 {
  color: #333;
  margin-bottom: 20px;
  font-size: 16px;
  font-weight: 600;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.stat-card {
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  text-align: center;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #666;
  font-size: 14px;
  margin-bottom: 10px;
}

.card-value {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
}

.card-desc {
  color: #999;
  font-size: 12px;
}

@media (max-width: 768px) {
  .section-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .filter-controls {
    justify-content: center;
  }
  
  .detail-item {
    flex-direction: column;
    gap: 5px;
  }
  
  .detail-item label {
    width: auto;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
}
</style>
